import React, { useEffect, useState, useCallback } from 'react';
import qs from 'qs';
import PropTypes from 'prop-types';
import {
  get,
  split,
  zipObject,
  isEmpty,
  map,
  trim,
  size,
  omitBy,
  debounce
} from 'lodash/fp';
import { InputGroup, Input, Icon, HelpBlock } from 'rsuite';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import CreatePanels from '../../../constants/reservation/CreatePanels';
import { clearGuests, onChangeColumnsInput } from '../../../redux/actions';
import { getColumnsInput } from '../../../redux/selectors/components/guest-search';
import { switchCreateReservationPanel } from '../../../redux/Reservation/actions/routes/create';

const DLMTR = ';';
const DlmtrRegex = new RegExp(DLMTR, 'g');
const DividerRegex = new RegExp(`${DLMTR} `, 'g');
const getSelectionStart = (inputValue, nextStartIndex) => {
  const isFirstCharSpace = inputValue.substr(nextStartIndex, 1) === ' ';
  return isFirstCharSpace ? nextStartIndex + 1 : nextStartIndex;
};

const ColumnsInput = ({
  defaultValue,
  columns,
  onSubmit,
  onSwitchColumn,
  onSwitchSearch,
  ...props
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const columnsInput = useSelector(getColumnsInput);
  const [inputValue, setInputValue] = useState(columnsInput);
  const [selectedColumnIndex, setSelectedColumnIndex] = useState(0);

  const submitSearch = useCallback(
    value => {
      const values = map(trim, split(DLMTR, value));
      const query = omitBy(prop => !prop, zipObject(columns, values));
      dispatch(onChangeColumnsInput(inputValue));
      onSubmit(query);
    },
    [columns, inputValue]
  );

  useEffect(
    () => {
      const guestQuery = qs.parse(location.search, { ignoreQueryPrefix: true });
      if (!get('firstName', guestQuery)) {
        setInputValue('');
        dispatch(clearGuests());
        dispatch(onChangeColumnsInput(''));
        dispatch(switchCreateReservationPanel(CreatePanels.SEARCH));
      }
    },
    [location]
  );

  useEffect(
    () => {
      if (!defaultValue) return;

      setInputValue(defaultValue);
      submitSearch(defaultValue);
    },
    [defaultValue]
  );

  useEffect(
    () => {
      if (!isEmpty(columns)) {
        onSwitchColumn(get(selectedColumnIndex, columns));
      }
    },
    [selectedColumnIndex, columns]
  );

  const debounceSwitchColumn = debounce(20)(elem => {
    const { value, selectionEnd } = elem;
    const columnKey = size(value.substr(0, selectionEnd).match(DlmtrRegex));
    setSelectedColumnIndex(columnKey);
  });

  const handleChange = value => {
    setInputValue(value);
  };

  const handleKeyPress = e => {
    if (e.key === 'Tab') return;

    if (e.key === 'Enter') {
      submitSearch(inputValue);
    }
  };

  const handleKeyDown = e => {
    if (e.key !== 'Tab' && e.key !== DLMTR) return;

    e.preventDefault();
    const { shiftKey, target: elem } = e;

    if (shiftKey) {
      if (elem.selectionStart === 0) {
        onSwitchSearch('barcode');
        return;
      }
      const subString = inputValue.substr(0, elem.selectionEnd);
      const selectedEnd = subString.lastIndexOf(DLMTR);
      const nextStartIndex =
        subString.substr(0, selectedEnd).lastIndexOf(DLMTR) + 1;
      const selectionStart = getSelectionStart(inputValue, nextStartIndex);
      elem.setSelectionRange(selectionStart, selectedEnd);
    } else {
      const delimiterCount = size(inputValue.match(DlmtrRegex));
      const isEndSelectionCursor = elem.selectionEnd === size(inputValue);
      const nextStartIndex = inputValue.indexOf(DLMTR, elem.selectionStart) + 1;
      const selectionStart = getSelectionStart(inputValue, nextStartIndex);

      if (!isEndSelectionCursor) {
        const selectionEnd = inputValue.indexOf(DLMTR, selectionStart);
        elem.setSelectionRange(selectionStart, selectionEnd);
      } else if (delimiterCount < size(columns) - 1) {
        const value = `${inputValue}${DLMTR} `;
        setInputValue(value);
      }

      const inputChanged =
        inputValue.replace(DividerRegex, '') !==
        columnsInput.replace(DividerRegex, '');

      if (inputChanged) submitSearch(inputValue);
    }
  };

  const handleKeyUp = e => {
    debounceSwitchColumn(e.target);
  };

  const handleClick = e => {
    debounceSwitchColumn(e.target);
  };

  const handleFocus = e => {
    debounceSwitchColumn(e.target);
  };

  const handleSwitchColumns = (shiftKey = false) => () => {
    const input = get('inputRef.current', props);
    input.focus();
    handleKeyDown({
      shiftKey,
      key: DLMTR,
      target: input,
      preventDefault: () => {}
    });
    debounceSwitchColumn(input);
  };

  return (
    <div>
      <InputGroup size="lg">
        <InputGroup.Button onClick={handleSwitchColumns(true)}>
          <Icon icon="left" />
        </InputGroup.Button>
        <InputGroup.Button onClick={handleSwitchColumns()}>
          <Icon icon="right" />
        </InputGroup.Button>
        <Input
          {...props}
          value={inputValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          onKeyPress={handleKeyPress}
          onClick={handleClick}
          onFocus={handleFocus}
          placeholder="Press Enter or Search icon to submit"
        />
        <InputGroup.Button
          onClick={() => {
            submitSearch(inputValue);
          }}
        >
          <Icon icon="search" />
        </InputGroup.Button>
      </InputGroup>
      <HelpBlock style={{ margin: 3 }}>
        <small>
          <Icon icon="info" />
          {` Use Arrows, Tab or Semicolon "${DLMTR}" to change columns`}
        </small>
      </HelpBlock>
    </div>
  );
};

ColumnsInput.propTypes = {
  defaultValue: PropTypes.string,
  columns: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSubmit: PropTypes.func.isRequired,
  onSwitchColumn: PropTypes.func.isRequired,
  onSwitchSearch: PropTypes.func.isRequired
};

ColumnsInput.defaultProps = {
  defaultValue: ''
};

export default ColumnsInput;
